import React, { forwardRef, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import Icon, { IconSizes } from '@polestar/component-warehouse-react/components/icon/Icon';
import { MediaType, useMediaProvider, } from '@polestar/component-warehouse-react/components/video-player/internal/VideoPlayerContext';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { m as motion, useMotionValue, useTransform } from 'framer-motion';
import { css } from '@emotion/react';
import { useButton } from '@polestar/component-warehouse-react/components/button/Button.helpers';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { focusProviderOptions } from '@polestar/component-warehouse-react/contexts/focus';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { minimalHitbox } from '@polestar/component-warehouse-react/helpers/style';
const size = 320;
const center = size / 2;
const strokeWidth = 20;
const radius = (size - strokeWidth) / 2;
const maxArray = radius * (Math.PI * 2);
const PauseButton = forwardRef(({ mediaRef, ariaLabels, onClick, indicator = true, ...props }, ref) => {
    const buttonEvents = useButton(props);
    const progress = useMotionValue(0);
    const { playing, togglePlaying, duration, currentTime } = useMediaProvider({
        mediaRef,
        type: MediaType.VIDEO,
    });
    // ensure at end of video the stroke is fully visible
    if (currentTime.get() === duration) {
        progress.set(0);
    }
    useEffect(() => {
        if (indicator) {
            progress.set(currentTime.get() / duration || 0);
            return currentTime.on('change', (latestTime) => {
                progress.set(latestTime / duration);
            });
        }
        else {
            progress.set(0);
        }
    }, [currentTime, duration, indicator, progress]);
    const handleClick = useCallback((e) => {
        togglePlaying();
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    }, [onClick, togglePlaying]);
    const strokeDasharray = useTransform(progress, (latestProgress) => {
        const progress = Math.round(maxArray * latestProgress);
        return `${maxArray - progress} ${progress}`;
    });
    const strokeDashoffset = useTransform(progress, (latestProgress) => {
        const progress = Math.round(maxArray * latestProgress);
        return maxArray * 0.25 + (maxArray - progress);
    });
    return (React.createElement(Container, { ...props, ...buttonEvents, type: "button", onClick: handleClick, ref: ref, "aria-label": playing ? ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.pause : ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.play, ...focusProviderOptions({ round: true }) },
        React.createElement(Icon, { icon: !playing ? Icons.playFilled : Icons.pause, pairedWithText: true, topOffset: false, size: IconSizes.medium }),
        React.createElement(Svg, { width: size, height: size, viewBox: `0 0 ${size} ${size}` },
            React.createElement(Circle, { shapeRendering: "geometricPrecision", fill: "none", strokeWidth: strokeWidth, strokeLinecap: "butt", cx: center, cy: center, r: radius, style: {
                    // @ts-ignore
                    strokeDasharray,
                    // @ts-ignore
                    strokeDashoffset,
                } }))));
});
PauseButton.displayName = 'PauseButton';
export default PauseButton;
const Container = styled.button(({ theme, active }) => css(css `
      cursor: pointer;
      display: flex;
      flex-grow: 1;
      flex-direction: column;
      position: relative;
      justify-content: center;
      align-items: center;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
      background: none;
      border: none;
      color: ${theme.pauseButton.states.enabled.color};
      appearance: none;
      width: ${Spacings.xLarge};
      height: ${Spacings.xLarge};
      max-width: ${Spacings.xLarge};
      max-height: ${Spacings.xLarge};
      margin: 0;
      padding: 0;
      border-radius: 50%;
      line-height: 0;

      circle {
        stroke: ${theme.pauseButton.states.enabled.color};
      }

      &:disabled {
        pointer-events: none;
        color: ${theme.pauseButton.states.disabled.color};

        circle {
          stroke: ${theme.pauseButton.states.disabled.color};
        }
      }
    `, minimalHitbox, !active
    ? css `
          @media (hover: hover) {
            &:hover:not(:disabled) {
              color: ${theme.pauseButton.states.hover.color};

              circle {
                stroke: ${theme.pauseButton.states.hover.color};
              }
            }
          }
        `
    : css `
          color: ${theme.pauseButton.states.active.color};

          circle {
            stroke: ${theme.pauseButton.states.active.color};
          }
        `));
const Svg = styled.svg `
  position: absolute;
  width: 100%;
  height: 100%;
`;
const Circle = motion(styled.circle `
  transition: ${createCSSTransition('stroke', MotionEasings.default, MotionDurations.fast)};
`);
